import { EmptyState, IconCallIncoming, PageContainer, Skeleton } from '@ccp/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useInteraction, useMyWork } from '../../features/interactions/api.js';
import { ActiveInteraction } from '../../features/interactions/components/active-interaction.js';
import { AnswerAcceptedWork } from '../../features/interactions/components/answer-accepted-work.js';
import { Dialler } from '../../features/interactions/components/dialler.js';
import { OfferToast } from '../../features/interactions/components/offer-toast.js';
import { ChatTabs } from '../../features/live-chat/components/chat-tabs.js';
import { useAgentSession, useInteractionStream } from '../../session/session-provider.js';

export const Route = createFileRoute('/agent/')({ component: AgentWorkspace });

/**
 * The agent's screen: what is being offered, and what is being worked.
 *
 * Two sources, and the division is deliberate. **Offers** come from the socket, through the session
 * machine: an offer lives for seconds, and waiting for a read to confirm it would spend the time the
 * agent has to answer. **Work** comes from `GET /v1/interactions/mine`, because the opposite is true
 * of a call in progress — it outlives the tab, and a screen that only knew what it had been told
 * would come back blank after a reload with the caller still on the line.
 *
 * The two meet when an offer is accepted: the platform emits the interaction going active, the
 * applier invalidates both the offer list and `mine`, and the call appears here as work. Nothing in
 * this file has to sequence that, which is why there is no local "accepting" state to get stuck in.
 *
 * What this screen does own is **which interactions the socket is following**. An offer arrives on
 * `agent:<id>`, but everything that happens to the call afterwards — held, resumed, transferred,
 * ended by the other party — belongs to the Interaction aggregate and travels on
 * `interaction:<id>`. A screen that never subscribed would show a live call that stopped moving.
 */
function AgentWorkspace() {
  const { t } = useTranslation('interactions');
  const offers = useAgentSession((snapshot) => snapshot.context.offers);
  const wrapUpSeconds = useAgentSession((snapshot) => snapshot.context.wrapUpSeconds);
  const chatCapacity = useAgentSession(
    (snapshot) =>
      snapshot.context.capacities.find((capacity) => capacity.channel === 'chat')?.capacity,
  );
  const work = useMyWork();
  // Destructured, because the hook returns a fresh object each render while the two functions
  // themselves are stable: depending on the object would unfollow and re-follow every call on
  // every render, which is a TOPICS frame per keystroke.
  const { follow, unfollow } = useInteractionStream();

  // Follow exactly what is on screen: every offer and every call being worked, and nothing else. A
  // call that has gone is unfollowed, which also forgets its sequence — the client is no longer
  // entitled to notice a gap in a stream it has stopped reading.
  const following = useRef<readonly string[]>([]);
  const onScreen = [
    ...offers.map((offer) => offer.interactionId),
    ...(work.data ?? []).map((item) => item.interaction.id),
  ];
  const key = onScreen.join(',');
  const working = work.data ?? [];
  const chats = working.filter((item) => item.interaction.channel === 'chat');
  const calls = working.filter((item) => item.interaction.channel !== 'chat');
  useEffect(() => {
    const wanted = key === '' ? [] : key.split(',');
    for (const interactionId of wanted) {
      if (!following.current.includes(interactionId)) {
        follow(interactionId);
      }
    }
    for (const interactionId of following.current) {
      if (!wanted.includes(interactionId)) {
        unfollow(interactionId);
      }
    }
    following.current = wanted;
  }, [key, follow, unfollow]);

  // Leaving the screen stops the subscriptions: an agent who has navigated away is a socket
  // carrying events nobody will read. The call itself is unaffected — this is what is *watched*.
  useEffect(
    () => () => {
      for (const interactionId of following.current) {
        unfollow(interactionId);
      }
      following.current = [];
    },
    [unfollow],
  );

  return (
    // One column a conversation can be read in. It ran the whole width of the screen, so on a
    // laptop the number field was 1350 pixels long and a transcript line twice a readable measure
    // (M5.5-01).
    <PageContainer width="medium">
      {/* The agent's leg, answered: the accept on this screen is the answer. */}
      <AnswerAcceptedWork />
      {/* Calling out, above the work rather than inside it: a call an agent places is not something
          they were handed, and it belongs where they look when they have nothing in front of them. */}
      <Dialler />
      {/*
        **Offers are out of the document flow, and that is a correctness fix rather than a visual
        one.**

        `OfferToast` was a toast in name only: it rendered in the column, third of four, so an
        offer arriving while the agent was on a call pushed the live call — and its hold, transfer
        and hang-up buttons — down the page by the height of the card, then pulled them back up
        when the offer expired or was taken. An agent reaching for Hang up at the moment a call is
        offered would press whatever had moved into its place. Nothing about the layout said this
        was happening; it only happens when two things are true at once.

        So the live call owns the page and an offer floats above it. Anchored to the bottom right
        because the header's height is not a token this file could position against, and capped at
        a panel's width so the stack is the size of its cards rather than an invisible sheet over
        the screen that swallows clicks.

        `flex-col-reverse` puts the **oldest** offer nearest the bottom edge: it is the one bound
        to the answer key, so the offer the keyboard will take is the one closest to where the eye
        rests.
      */}
      {offers.length > 0 ? (
        <section
          aria-label={t('offer.label')}
          className="fixed right-(--space-lg) bottom-(--space-lg) z-(--elevation-toast) flex w-full max-w-(--width-panel) flex-col-reverse gap-(--space-sm)"
        >
          {offers.map((offer, index) => (
            // The answer key goes to the oldest offer only: two toasts both bound to it would both
            // accept, and the agent would have taken a call they never chose.
            <OfferToast key={offer.interactionId} offer={offer} shortcuts={index === 0} />
          ))}
        </section>
      ) : null}

      {work.isPending ? (
        <div role="status" aria-busy="true" aria-label={t('active.loading')}>
          <Skeleton className="h-40 w-full" />
        </div>
      ) : work.isError ? (
        <p role="status" className="text-(length:--font-size-body) text-(--color-danger)">
          {t('active.unavailable')}
        </p>
      ) : (work.data ?? []).length === 0 ? (
        <EmptyState
          icon={IconCallIncoming}
          title={offers.length > 0 ? t('active.answerFirst') : t('active.nothing')}
        />
      ) : (
        <>
          {/*
            Calls stack and chats tab, which is the shape of the two channels rather than a
            preference. A call is one conversation an agent is in with their voice and there is at
            most one of it; chats are up to the agent's capacity, each with a transcript and a
            composer, and three of those down one page is a screen nobody can answer from.
          */}
          {calls.map((item, index) => (
            <WorkItem
              key={item.interaction.id}
              interactionId={item.interaction.id}
              {...(wrapUpSeconds === undefined ? {} : { wrapUpSeconds })}
              // Only the first call gets the keyboard. An agent on two interactions has one pair
              // of hands, and a hold key that acted on both would be a key nobody could use.
              shortcuts={index === 0}
            />
          ))}
          <ChatTabs
            interactionIds={chats.map((item) => item.interaction.id)}
            {...(chatCapacity === undefined ? {} : { capacity: chatCapacity })}
          >
            {(interactionId) => (
              <WorkItem
                interactionId={interactionId}
                {...(wrapUpSeconds === undefined ? {} : { wrapUpSeconds })}
                // No keyboard in a chat pane: `Alt+T` and the hold key are a call's, and the
                // thing an agent presses in a chat is Enter in the composer.
                shortcuts={false}
              />
            )}
          </ChatTabs>
        </>
      )}
    </PageContainer>
  );
}

interface WorkItemProps {
  readonly interactionId: string;
  readonly wrapUpSeconds?: number;
  readonly shortcuts: boolean;
}

/**
 * One item of work, read back in full.
 *
 * `mine` says which interactions are the agent's; this reads each one through the same query the
 * screen pop warmed, so the open offer and the active assignment come with it and the panel is
 * drawn from one shape rather than from two that could disagree.
 */
function WorkItem({ interactionId, wrapUpSeconds, shortcuts }: WorkItemProps) {
  const { t } = useTranslation('interactions');
  const detail = useInteraction(interactionId);

  if (detail.data === undefined) {
    return detail.isError ? (
      <p role="status" className="text-(length:--font-size-caption) text-(--color-danger)">
        {t('active.unavailable')}
      </p>
    ) : (
      <div role="status" aria-busy="true" aria-label={t('active.loading')}>
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <ActiveInteraction
      detail={detail.data}
      {...(wrapUpSeconds === undefined ? {} : { wrapUpSeconds })}
      shortcuts={shortcuts}
    />
  );
}
